import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import ModalWrapper from '@/components/General/ModalWrapper';
import InputSection from '@/components/General/inputs/InputSection';
import Chip from '@/components/General/Chip';

export default function BoardInfoScreen() {
    const { boardID } = useLocalSearchParams();
    const router = useRouter();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: board, isLoading: boardLoading } = Query.boards.byId({id: boardID, options: {enabled: !!boardID}});
    const { data: members, isLoading: membersLoading } = Query.boards.members({boardID, options: {enabled: !!boardID}});

    const isLoading = boardLoading || membersLoading;

    if(isLoading){
        return (
            <ModalWrapper title="Board Info">
                <View style={styles.loaderContainer}>
                    <ActivityIndicator size="large"/>
                </View>
            </ModalWrapper>
        );
    }

    if(!board){
        return (
            <ModalWrapper title="Board Info">
                <Text style={styles.emptyText}>Board not found.</Text>
            </ModalWrapper>
        );
    }

    const createdBy = members?.find(p => p?.id == board.createdByID);
    const roles = board.Roles?.map(r => r.Role).filter(Boolean) || [];

    return (
        <ModalWrapper title="Board Info">
            <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                <View style={styles.avatarSection}>
                    <View style={styles.boardIconBubble}>
                        <Unicon name="people-outline" size={36} colorVariant="primary"/>
                    </View>
                </View>

                <InputSection title="Details">
                    <View style={styles.detailRow}>
                        <Text style={styles.detailLabel}>Name</Text>
                        <Text style={styles.detailValue}>{board.name}</Text>
                    </View>
                    {!!board.description && (
                        <View style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Description</Text>
                            <Text style={styles.detailValue}>{board.description}</Text>
                        </View>
                    )}
                    {createdBy && (
                        <View style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Created By</Text>
                            <Text style={styles.detailValue}>{createdBy.firstName} {createdBy.lastName}</Text>
                        </View>
                    )}
                    {board.createdAt && (
                        <View style={styles.detailRow}>
                            <Text style={styles.detailLabel}>Created</Text>
                            <Text style={styles.detailValue}>{new Date(board.createdAt).toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}</Text>
                        </View>
                    )}
                </InputSection>

                {roles.length > 0 && (
                    <InputSection title={`Roles (${roles.length})`}>
                        <View style={styles.memberList}>
                            {roles.map((role) => {
                                const boardRole = board.Roles?.find(r => r.roleID == role.id);
                                return (
                                    <View key={role.id} style={styles.memberRow}>
                                        <View style={styles.roleIconBubble}>
                                            <Unicon name="people-outline" size={18} colorVariant="primary"/>
                                        </View>
                                        <View style={styles.memberInfo}>
                                            <Text style={styles.memberName} numberOfLines={1}>
                                                {role.name}
                                            </Text>
                                            <View style={styles.roleBadges}>
                                                {boardRole?.isAdmin && <Chip colorVariant="primary" textStyle={{fontSize: 9}}>Admin</Chip>}
                                                {boardRole?.canPost && <Chip colorVariant="textSecondary" textStyle={{fontSize: 9}}>Post</Chip>}
                                                {boardRole?.canComment && <Chip colorVariant="textSecondary" textStyle={{fontSize: 9}}>Comment</Chip>}
                                            </View>
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </InputSection>
                )}

                <InputSection title={`Members (${members?.length || 0})`}>
                    <View style={styles.memberList}>
                        {members?.map((person) => (
                            <View key={person.id} style={styles.memberRow}>
                                <Avatar size={36} profile={person} disableBorder onPress={() => router.push(`/people/${person.id}`)}/>
                                <View style={styles.memberInfo}>
                                    <Text style={styles.memberName} numberOfLines={1}>
                                        {person.firstName} {person.lastName}
                                    </Text>
                                    {person.id == board.createdByID && (
                                        <Text style={styles.memberBadge}>Admin</Text>
                                    )}
                                    {person.id == loggedInUser?.id && (
                                        <Text style={styles.memberBadge}>You</Text>
                                    )}
                                </View>
                            </View>
                        ))}
                    </View>
                </InputSection>
            </ScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors, fonts, spacing}) => ({
    loaderContainer: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyText: {
        fontSize: 15,
        color: colors.textSecondary,
        textAlign: 'center',
        padding: 30,
    },
    scrollContent: {
        paddingBottom: spacing.xl,
    },
    avatarSection: {
        alignItems: 'center',
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
    },
    boardIconBubble: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    detailLabel: {
        fontSize: 14,
        color: colors.textSecondary,
        fontFamily: fonts.body,
    },
    detailValue: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body,
        flex: 1,
        textAlign: 'right',
    },
    memberList: {
        gap: 4,
    },
    memberRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 6,
        paddingHorizontal: 4,
        borderRadius: 10,
    },
    memberInfo: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    memberName: {
        flex: 1,
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.text,
    },
    memberBadge: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.primary,
        backgroundColor: colors.bgPrimary,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
        overflow: 'hidden',
    },
    roleIconBubble: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roleBadges: {
        flexDirection: 'row',
        gap: 4,
    },
}));
